import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';

const PricingPage = () => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(5);
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    // Load Stripe
    const initStripe = async () => {
      const stripeInstance = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      setStripe(stripeInstance);
    };
    initStripe();
  }, []);

  const calculatePrice = (users) => {
    if (users <= 5) return 10;
    const additionalUsers = users - 5;
    return 10 + (additionalUsers * 5);
  };

  const handleSubscribe = async () => {
    if (!stripe) {
      alert('Stripe is not loaded yet. Please try again.');
      return;
    }

    setLoading(true);
    try {
      // Create subscription on backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscription/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customerId: 'new',
          numberOfUsers: selectedPlan,
          email: 'customer@example.com' // This would come from user auth
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      const { clientSecret } = await response.json();

      // Redirect to Stripe Checkout
      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: {
            // This would be collected from a form
            number: '4242424242424242',
            exp_month: 12,
            exp_year: 2025,
            cvc: '123',
          },
        },
      });

      if (error) {
        alert(`Payment failed: ${error.message}`);
      } else {
        alert('Subscription created successfully!');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const plans = [
    { users: 5, price: 10, popular: true },
    { users: 10, price: 35 },
    { users: 20, price: 85 },
    { users: 50, price: 235 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 py-12 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Start with 5 users for just $10/month. Add more users as your team grows for only $5/month each.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {plans.map((plan) => (
            <div
              key={plan.users}
              className={`relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border-2 transition-all hover:scale-105 ${
                plan.popular 
                  ? 'border-purple-500 shadow-2xl shadow-purple-500/25' 
                  : 'border-white/20 hover:border-white/40'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">
                  {plan.users} Users
                </div>
                <div className="text-6xl font-bold text-purple-400 mb-4">
                  ${plan.price}
                </div>
                <div className="text-gray-300 mb-6">per month</div>
                
                <div className="text-sm text-gray-400 mb-6">
                  ${(plan.price / plan.users).toFixed(2)} per user
                </div>

                <button
                  onClick={() => setSelectedPlan(plan.users)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
                  }`}
                >
                  {plan.popular ? 'Get Started' : 'Choose Plan'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Plan Builder */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 mb-16">
          <h2 className="text-3xl font-bold text-white text-center mb-8">
            Build Your Custom Plan
          </h2>
          
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-4 mb-8">
              <label className="text-white text-lg">Number of Users:</label>
              <input
                type="range"
                min="1"
                max="100"
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(parseInt(e.target.value))}
                className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
              />
              <span className="text-2xl font-bold text-purple-400 min-w-[60px]">
                {selectedPlan}
              </span>
            </div>

            <div className="text-center mb-8">
              <div className="text-4xl font-bold text-white mb-2">
                ${calculatePrice(selectedPlan)}/month
              </div>
              <div className="text-gray-300">
                {selectedPlan <= 5 
                  ? `Base plan: $10 for ${selectedPlan} users`
                  : `Base plan: $10 for 5 users + $${(selectedPlan - 5) * 5} for ${selectedPlan - 5} additional users`
                }
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={handleSubscribe}
                disabled={loading || !stripe}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all"
              >
                {loading ? 'Processing...' : 'Start Subscription'}
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Enterprise Security</h3>
            <p className="text-gray-300">Bank-level security with SSO, 2FA, and role-based access control</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">AI-Powered</h3>
            <p className="text-gray-300">Advanced AI features for compliance monitoring and enterprise insights</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">Scalable</h3>
            <p className="text-gray-300">Grow your team seamlessly with our flexible pricing model</p>
          </div>
        </div>

        {/* FAQ */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
          <h2 className="text-3xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="border-b border-white/20 pb-4">
              <h3 className="text-lg font-semibold text-white mb-2">
                Can I change my plan later?
              </h3>
              <p className="text-gray-300">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </div>

            <div className="border-b border-white/20 pb-4">
              <h3 className="text-lg font-semibold text-white mb-2">
                What happens if I exceed my user limit?
              </h3>
              <p className="text-gray-300">
                You'll be notified when you're approaching your limit. You can upgrade your plan to add more users.
              </p>
            </div>

            <div className="border-b border-white/20 pb-4">
              <h3 className="text-lg font-semibold text-white mb-2">
                Is there a setup fee?
              </h3>
              <p className="text-gray-300">
                No setup fees! You only pay the monthly subscription cost.
              </p>
            </div>

            <div className="pb-4">
              <h3 className="text-lg font-semibold text-white mb-2">
                Can I cancel anytime?
              </h3>
              <p className="text-gray-300">
                Yes, you can cancel your subscription at any time. No long-term contracts required.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for slider */}
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: linear-gradient(to right, #9333ea, #2563eb);
          cursor: pointer;
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: linear-gradient(to right, #9333ea, #2563eb);
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default PricingPage;
